import React, { Dispatch, useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Button, Card, CircularProgress, FormControl, TextField } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { FmtpError, FmtpErrorCode } from '@lanterds/types';
import './OSDManifestForm.scss';
import Header from 'src/components/common/Header/Header';
import { getOsdManifestRoutes } from 'src/api/reports';
import { SnackbarKey, useSnackbar } from 'notistack';

type OsdFormProps = {
    handleGenerateReport: () => void;
    isGeneratingReport: boolean;
    selectedRoute: string | null;
    setSelectedRoute: Dispatch<React.SetStateAction<string | null>>;
};

interface FieldErrors {
    routeError?: string;
}

export default function OSDForm({ handleGenerateReport, isGeneratingReport, selectedRoute, setSelectedRoute }: OsdFormProps) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [routes, setRoutes] = useState<string[] | null>(null);
    const [isFetchingRoutes, setIsFetchingRoutes] = useState<boolean>(false);
    const snackbarRef = useRef<SnackbarKey | null>(null);

    useEffect(() => {
        return () => {
            if (snackbarRef.current) {
                closeSnackbar(snackbarRef.current);
                snackbarRef.current = null;
            }
        };
    }, [closeSnackbar]);

    useEffect(() => {
        if (selectedRoute && fieldErrors) {
            setFieldErrors({});
        }
    }, [selectedRoute]);

    useEffect(() => {
        if (!routes) {
            snackbarRef.current = enqueueSnackbar('Getting Routes...', {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
            setIsFetchingRoutes(true);
            getOsdManifestRoutes()
                .then((data) => {
                    setRoutes(data);
                })
                .catch((err) => {
                    const message = 'An error occurred while getting routes';
                    const errorCode: FmtpErrorCode = 'SHU017';
                    console.error(err);
                    throw new FmtpError({
                        message,
                        errorCode,
                    });
                })
                .finally(() => {
                    if (snackbarRef.current) {
                        closeSnackbar(snackbarRef.current);
                        snackbarRef.current = null;
                    }
                    setIsFetchingRoutes(false);
                });
        }
    }, []);

    const validateForm = () => {
        let isValid = true;
        const newFieldErrors: FieldErrors = {};

        if (!selectedRoute) {
            newFieldErrors.routeError = 'Please select a Route.';
            isValid = false;
        }

        if (isValid) {
            setFieldErrors({});
        } else {
            setFieldErrors(newFieldErrors);
        }

        return isValid;
    };

    const handleGenerate = () => {
        const isValid = validateForm();
        if (isValid) {
            handleGenerateReport();
        }
    };

    return (
        <Box id="osd-container">
            <Card id="osd-form">
                <Grid xs={12} container spacing={2}>
                    <Grid xs={12}>
                        <Header text="OS&D Manifest" />
                    </Grid>
                    <Grid xs={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                options={routes ?? []}
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                loading={isFetchingRoutes}
                                isOptionEqualToValue={(option, value) => option === value}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Route"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isFetchingRoutes ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        InputLabelProps={{ ...params.InputLabelProps }}
                                        error={!!fieldErrors?.routeError}
                                        helperText={fieldErrors?.routeError}
                                    />
                                )}
                                value={selectedRoute}
                                onChange={(_event, newValue) => {
                                    setSelectedRoute(newValue);
                                }}
                                disabled={isFetchingRoutes}
                            />
                        </FormControl>
                    </Grid>
                    <Grid container xs={12} justifyContent="center">
                        <Grid>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={handleGenerate}
                                disabled={isGeneratingReport || isFetchingRoutes}
                            >
                                {isGeneratingReport ? 'Generating…' : 'Generate Report'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>
        </Box>
    );
}
